'use strict';

const _ = require('lodash');

/** @OverlayStore */
const Store = function (ResourceHelper, Flux, APP_DISPATCHER_ID, SiteStore, MapActions, NavActions, TopControlsActions, AnalyticsService) {

  // Intialize the store
  const OverlayStore = Flux.Store.create({
    name: 'OverlayStore',
    events: {
      Changed: true,
      ActiveSaved: true,
      OverlayDestroyed: true,
      PermissionError: false
    }
  });

  /**
   * Initial empty store state
   * @var {Object} $state
   */
  OverlayStore.$state = {
    current : null,
    available : null,
    active : null
  };

  const store = OverlayStore;

  // Cached version of the overlay before changes
  OverlayStore._activeOverlayBeforeEdit = null;

  /**
   * Intialize the store bindings and initial data.
   * @constructor
   */
  OverlayStore.init = () => {

    const d = Flux.Dispatcher.get(APP_DISPATCHER_ID);

    d.register(MapActions.EditOverlayStart, store.handleEditOverlayStart.bind(store));
    d.register(MapActions.SaveActiveOverlay, store.handleSaveActiveOverlay.bind(store));
    d.register(MapActions.EditOverlayEnd, store.handleEditOverlayEnd.bind(store));
    d.register(MapActions.ChangeOverlayOpacity, store.handleOpacityChanged.bind(store));
    d.register(MapActions.DestroyOverlay, store.handleDestroyOverlay.bind(store));
    d.register(NavActions.ContinueWithNavigation, store.continueWithNavigation.bind(store));
    d.register(TopControlsActions.ContinueWithNavigation, store.continueWithNavigation.bind(store));
  };

   /**
   * Sets the internal state for the Store.
   * @param {state} object An OverlayStore state object.
   */
  OverlayStore.setState = (state) => {

    angular.extend(OverlayStore.$state, state);
    OverlayStore.emit(OverlayStore.Events.Changed, OverlayStore.$state);
  };

  /**
   * Adds the overlay with id `id` to the list of `current` Overlays,
   * checking first that it is part of the `available` overlay list
   */
  OverlayStore.activate = (id) => {

    const available = OverlayStore.get('available');
    const current = OverlayStore.get('current');
    if (!current) {
      // Should not enter here, current should be at least an empty array
      return;
    }
    // Do nothing if it's already active
    if (!_.find(current, 'id', id)) {
      const element = _.find(available, 'id', id);
      // Requested an overlay that was not available
      if (!element) {
        console.error('Annotation annotation not found: ', id);
      }
      else {
        const site = SiteStore.get('current');
        AnalyticsService.track('[CORE] Overlays', 'View Overlay', {
          overlayId: element.id,
          siteId: site.id,
          siteName: site.name
        });

        OverlayStore.setState({
          current : current.concat([element]) // Force a new array to trigger changes
        });
      }
    }
  };

  /**
   * Removes the annotation with id `id` to the list of `current` Overlay
   * if present
   */
  OverlayStore.deactivate =  (id) => {

    const current = OverlayStore.get('current');
    if (!current) {
      return;
    }
    const element = _.find(current, 'id', id);
    if (element) {
      OverlayStore.setState({
        current : _.reject(current, 'id', id) // Force a new array to trigger changes
      });
    }
  };

  OverlayStore.getPermissions = (overlayId) => {

    const available = OverlayStore.get('available');
    const overlay = _.find(available, 'id', overlayId); // Grab store own overlay

    const permissions = overlay._permissions;

    // Handle case of newly created overlays that don't have yet its _permissions
    // object from API
    const UserStore = Flux.Store.get('UserStore');
    const currentUser = UserStore.get('user');
    if (overlay.userId === currentUser.id) {
      permissions.all = true;
    }

    return permissions;
  };

  OverlayStore.handleEditOverlayStart = (ev) => {

    const available = OverlayStore.get('available');
    const overlay = _.find(available, 'id', ev.overlayId);
    OverlayStore._activeOverlayBeforeEdit = _.cloneDeep(overlay);
    OverlayStore.setState({
      active: overlay
    });
  };

  OverlayStore.handleEditOverlayEnd = (ev) => {

    const save = ev.saveChanges;

    if (save !== undefined && !save) {
      OverlayStore._revertChanges();
    }
    OverlayStore.setState({
      active: null
    });
  };

  OverlayStore.handleSaveActiveOverlay = () => {

    const overlay = OverlayStore.get('active');
    if (!overlay) {
      return;
    }
    OverlayStore.emit(OverlayStore.Events.ActiveSaved);
  };

  OverlayStore._revertChanges = () => {

    const previous = OverlayStore._activeOverlayBeforeEdit;
    const active = OverlayStore.get('active');
    const Overlay = ResourceHelper.getResource('Overlay');

    if (!previous) {
      return;
    }

    if (previous.opacity) {
      active.opacity = previous.opacity;
    }
    else {
      delete active.opacity;
    }
  };

  OverlayStore.handleDestroyOverlay = (ev) => {

    const Overlay = ResourceHelper.getResource('Overlay');
    const overlay = _.find(OverlayStore.get('available'), 'id', ev.overlayId);
    const currentSite = SiteStore.get('current');
    if (!overlay) {
      return;
    }
    Overlay.destroy(overlay)
    .then(() => {

      Overlay.eject(overlay);
      OverlayStore.setState({
        available: currentSite.overlays.filter((overlay) => {

          return !overlay.status || overlay.status === 'completed';
        }),
        current: _.reject(OverlayStore.$state.current, 'id', overlay.id),
        active: null
      });
      AnalyticsService.track('[CORE] Overlays', 'Delete Overlay', {
        overlayId: overlay.id,
        siteId: currentSite.id,
        siteName: currentSite.name
      });
      OverlayStore.emit(OverlayStore.Events.OverlayDestroyed);
    })
    .catch((error) => {

      // Permission error
      if (error.status && error.status === 403) {

        OverlayStore.emit(OverlayStore.Events.PermissionError);
      }

      // Track error
      AnalyticsService.track('[CORE] Overlays', 'Delete Annotation Error', {
        overlayId: overlay.id,
        siteId: currentSite.id,
        siteName: currentSite.name,
        error: JSON.stringify(error.data.error)
      });
    });
  };

  OverlayStore.handleOpacityChanged = (ev) => {

    const overlay = _.find(OverlayStore.get('available'), 'id', ev.overlayId);
    overlay.opacity = ev.opacity;
  };

  OverlayStore.continueWithNavigation = () => {

    OverlayStore.handleEditOverlayEnd({saveChanges: false});
  };

  //////////////////////////////////////////////////////////////////////////////
  // Event Handlers
  //////////////////////////////////////////////////////////////////////////////

  store.handleSiteChange = () => {

    const currentSite = SiteStore.get('current');
    if (currentSite && currentSite.overlays) {
      OverlayStore.setState({
        available : currentSite.overlays.filter((overlay) => {

          return !overlay.status || overlay.status === 'completed';
        }),
        current : []
      });
    }
  };

  SiteStore.on(SiteStore.Events.Changed, store.handleSiteChange.bind(store));

  return OverlayStore;
};

Store.$inject = ['ResourceHelper', 'sbFlux', 'APP_DISPATCHER_ID', 'SiteStore', 'MapActions', 'NavActions', 'TopControlsActions', 'AnalyticsService'];
module.exports = Store;



// WEBPACK FOOTER //
// ./src/app/dash/dataStores/OverlayStore.js